import type { Post } from "../types";

const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

export type DiscoverType = "all" | "people" | "spaces" | "posts";
export type DiscoverSort = "for-you" | "newest" | "popular";
export type DiscoverItemKind = "person" | "space" | "post";

export type DiscoverItem = {
    kind: DiscoverItemKind;
    id: string;
    score?: number;
    title: string;
    subtitle?: string;
    content?: string;
    href?: string;
    because?: string;
    createdAt?: string;
    updatedAt?: string;
    username?: string;
    displayName?: string;
    avatarUrl?: string;
    author?: any;
    space?: any;
    reactions?: any;
    post?: Post;
    desc?: string;
    by?: string;
    members?: string;
    membersCount?: number;
    category?: string;
    accentColor?: string;
    accessMode?: string;
    isJoined?: boolean;
    recentPosts?: number;
    lastPostAt?: string | null;
};

export type DiscoverSections = {
    people: DiscoverItem[];
    spaces: DiscoverItem[];
    posts: DiscoverItem[];
};

export type DiscoverResponse = {
    success: boolean;
    query?: Record<string, unknown>;
    items: DiscoverItem[];
    sections: DiscoverSections;
    totals: {
        all: number;
        people: number;
        spaces: number;
        posts: number;
    };
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
    nextPage: number | null;
    nextCursor: string | null;
    message?: string;
};

export type FetchDiscoverParams = {
    q?: string;
    type?: DiscoverType;
    sort?: DiscoverSort;
    page?: number;
    cursor?: string | null;
    limit?: number;
    signal?: AbortSignal;
};

export type TrackDiscoverEventInput = {
    type: "impression" | "click";
    surface?: DiscoverType;
    query?: string;
    itemKind?: DiscoverItemKind;
    itemId?: string;
    position?: number;
    items?: Array<{ kind: DiscoverItemKind; id: string; position?: number }>;
};

const emptySections: DiscoverSections = { people: [], spaces: [], posts: [] };
const emptyTotals = { all: 0, people: 0, spaces: 0, posts: 0 };

export async function fetchDiscover(params: FetchDiscoverParams = {}): Promise<DiscoverResponse> {
    const url = new URL(`${API_BASE}/api/discover`);
    if (params.q) url.searchParams.set("q", params.q);
    url.searchParams.set("type", params.type || "all");
    url.searchParams.set("sort", params.sort || "for-you");
    url.searchParams.set("page", String(params.page || 1));
    if (params.cursor) url.searchParams.set("cursor", params.cursor);
    url.searchParams.set("limit", String(params.limit || 18));

    const res = await fetch(url.toString(), {
        headers: authHeaders(),
        signal: params.signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Discover failed: ${res.status}`);
    }
    return {
        success: Boolean(data.success),
        query: data.query || {},
        items: Array.isArray(data.items) ? data.items : [],
        sections: {
            people: data.sections?.people || emptySections.people,
            spaces: data.sections?.spaces || emptySections.spaces,
            posts: data.sections?.posts || emptySections.posts,
        },
        totals: {
            all: data.totals?.all || 0,
            people: data.totals?.people || 0,
            spaces: data.totals?.spaces || 0,
            posts: data.totals?.posts || 0,
        },
        page: data.page || 1,
        limit: data.limit || params.limit || 18,
        total: data.total || data.totals?.all || 0,
        hasMore: Boolean(data.hasMore),
        nextPage: data.nextPage || null,
        nextCursor: data.nextCursor || null,
        message: data.message,
    };
}

export async function trackDiscoverEvent(input: TrackDiscoverEventInput): Promise<void> {
    if (!localStorage.getItem("token")) return;
    const payload: Record<string, unknown> = {
        type: input.type,
        surface: input.surface || "all",
        query: input.query || "",
    };
    if (input.items) payload.items = input.items;
    else {
        payload.itemKind = input.itemKind;
        payload.itemId = input.itemId;
        payload.position = input.position || 0;
    }

    await fetch(`${API_BASE}/api/discover/track`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
        keepalive: true,
    }).catch(() => undefined);
}

export { emptySections, emptyTotals };
